import { useFormContext } from '@/shared/hooks/useForm'
import { Controller } from 'react-hook-form'
import type { z } from 'zod'
import Select from './Select'
import type { Currency } from '@/shared/models/moneyModel'
import type { Path, PathValue } from 'react-hook-form'

type ZodInfer<T extends z.ZodType> = z.infer<T>
type FieldValue<TSchema extends z.ZodObject<z.ZodRawShape>> = PathValue<ZodInfer<TSchema>, Path<ZodInfer<TSchema>>>

type SelectOption<TValue> = {
    value: TValue
    label: string
}

type ControlledCurrencySelectProps<TSchema extends z.ZodObject<z.ZodRawShape>> = {
    currencies: Currency[]
    name: Path<ZodInfer<TSchema>>
    label?: string
    placeholder?: string
    schema: TSchema
    onChange?: (option: SelectOption<TSchema> | null) => void
}

const ControlledCurrencySelect = <TSchema extends z.ZodObject<z.ZodRawShape>>({
    currencies,
    name,
    label,
    placeholder,
    schema,
    onChange,
    ...props
}: ControlledCurrencySelectProps<TSchema>) => {
    const { register, control } = useFormContext({ schema })

    const options: SelectOption<FieldValue<TSchema>>[] = currencies.map((currency) => ({
        // eslint-disable-next-line typescript/no-unsafe-type-assertion -- react-hook-form cannot type the value of a path the caller passes
        value: currency as FieldValue<TSchema>,
        label: currency
    }))

    return (
        <Controller
            name={name}
            control={control}
            rules={{ required: register(name).required }}
            render={({ field: { value, onChange: internalOnChange } }) => (
                <Select
                    {...register(name)}
                    isClearable
                    label={label}
                    placeholder={placeholder}
                    value={
                        value
                            ? {
                                  value: value,
                                  // eslint-disable-next-line typescript/no-unnecessary-type-assertion -- tsc needs it: the select option label is a string, and the field value is wider
                                  label: value as string
                              }
                            : null
                    }
                    onChange={(option) => {
                        onChange?.(option)
                        internalOnChange(option?.value || null)
                    }}
                    options={options}
                    {...props}
                />
            )}
        />
    )
}

export default ControlledCurrencySelect
